﻿import {currentCulture, DateOnly, DateTime, NamedValue, TimeOnly} from "@2d-front-monorepo/core";
import ParkingPrebookingAvailabilityType from "../enums/ParkingPrebookingAvailabilityType";
import {ParkingPrebookingAvailabilityEntry, SelectedParkingPrebookingAvailabilityEntry} from "../types/ParkingPrebookingAvailabilityEntry";
import {BasePermitRecurrence, ModifiedProperty, RecurrentPermitOptions} from "./BasePermitRecurrence";
import {isDateRangeValid} from "./isDateRangeValid";

export interface RecurrenceNoneOptions extends RecurrentPermitOptions {
}

//- Cuando hay una hora no seleccionable (ej: 19) el start no existe (correcto) pero el end tampoco (incorrecto).
//  Tiene que existir el end para poder seleccionar de las 18 a las 19.
export class RecurrenceNone extends BasePermitRecurrence {
	private _startTimes: NamedValue<TimeOnly>[] | undefined;
	private _endTimes: NamedValue<TimeOnly>[] | undefined;

	constructor(options: RecurrenceNoneOptions) {
		super(options);
	}

	// public override dayClassName(date: DateOnly): string | undefined {
	// 	if (this._alwaysValidWeekDays.includes(date.getDayOfWeek())) {
	// 		return undefined;
	// 	}
	//
	// 	return super.dayClassName(date);
	// }

	public override setAvailability(availability: ParkingPrebookingAvailabilityEntry[]): void {
		super.setAvailability(availability);
		this._createSegments(ModifiedProperty.All);
	}

	public override isValidEndDate(): boolean {
		return super.isValidEndDate() && this._operations.size === 1;
	}

	public override getSelected(): SelectedParkingPrebookingAvailabilityEntry[] {
		const result: SelectedParkingPrebookingAvailabilityEntry[] = [];
		const operation = this._operations.at(0);
		if (operation == null) {
			return result;
		}

		const operationStartDate = operation.startDate;
		const operationEndDate = operation.endDate;
		const startDateOnly = operationStartDate.toDateOnly();
		const endDateOnly = operationEndDate.toDateOnly();

		let currentSegmentStart: DateTime | null = null;
		let lastAvailableDate: DateOnly | null = null;

		for (let currentDate = startDateOnly; currentDate.compareTo(endDateOnly) <= 0; currentDate = currentDate.addDays(1)) {
			if (this._isUnavailable(currentDate)) {
				if (currentSegmentStart != null && lastAvailableDate != null) {
					result.push({
						startDate: currentSegmentStart,
						endDate: DateTime.fromDateAndTime(lastAvailableDate, TimeOnly.fromComponents(23, 59, 59))
					});
					currentSegmentStart = null;
					lastAvailableDate = null;
				}

			} else {
				if (currentSegmentStart == null) {
					if (currentDate.equals(startDateOnly)) {
						currentSegmentStart = operationStartDate;

					} else {
						currentSegmentStart = DateTime.fromDateAndTime(currentDate, TimeOnly.fromComponents(0, 0, 0));
					}
				}
				lastAvailableDate = currentDate;
			}
		}

		if (currentSegmentStart != null) {
			result.push({
				startDate: currentSegmentStart,
				endDate: operationEndDate
			});
		}

		return result;
	}

	public isSelectableStartDate(value: DateOnly): boolean {
		if (this._isDateInvalid(value)) {
			return false;
		}

		return this._getPrebookingTimes(value)?.available === true;
	}

	public isSelectableEndDate(value: DateOnly): boolean {
		if (this._isDateInvalid(value) || value < this._startDate) {
			return false;
		}

		if (!this._getPrebookingTimes(value)?.available) {
			return false;
		}

		if (!this._validateMaxConsecutiveDays(value)) {
			return false;
		}

		return isDateRangeValid(
			this._availability,
			DateTime.fromDateAndTime(this._startDate, this._startTime),
			DateTime.fromDateAndTime(value.addDays(1), TimeOnly.zero()),
			this._unavailable
		);
	}

	public override getAvailableStartTimes(): NamedValue<TimeOnly>[] {
		if (this._startTimes != null) {
			return this._startTimes;
		}

		const culture = currentCulture();
		const prebookingTimes = this._getPrebookingTimes(this._startDate);
		return this._startTimes = prebookingTimes ? prebookingTimes.times.map(x => {
			return {
				label: culture.formatTime(x.start, "short-time"),
				value: x.start
			};
		}) : [];
	}

	public override getAvailableEndTimes(): NamedValue<TimeOnly>[] {
		if (this._endTimes != null) {
			return this._endTimes;
		}

		const available = this._getPrebookingTimes(this._endDate)?.times;
		if (available == null || available.length === 0) {
			return this._endTimes = [];
		}

		const culture = currentCulture();
		const result: NamedValue<TimeOnly>[] = [];
		let startHour = 0;

		if (this._startDate.equals(this._endDate)) {
			//si el startDate y endDate son iguales, empezar a contar desde la hora actual.
			startHour = this._startTime.getHours();
		}

		//Primero busco el índice de startHour. No puedo usar la hora como índice porque puede haber entradas faltantes antes de esta hora.
		const startIndex = available.findIndex(x => x.start.getHours() === startHour);
		if (startIndex === -1) {
			//la disponibilidad del día no arranca en startHour (huecos o rango multi-día): sin horas de fin
			return this._endTimes = [];
		}

		for (let i = startIndex; i < available.length; ++i) {
			const {start, end} = available[i];
			const next = TimeOnly.fromComponents(startHour++, 0, 0);

			//busco horas consecutivas
			if (!start.equals(next)) {
				break;
			}

			result.push({
				label: culture.formatTime(end, "short-time"),
				value: end
			});
		}

		return this._endTimes = result;
	}

	public override isValid(): boolean {
		return super.isValid() && this._operations.size === 1;
	}

	protected override _createSegments(property: ModifiedProperty): void {
		// Every date/time/availability mutation funnels through here. Invalidating in the
		// setters missed _updateAllDates' direct _startDate/_endDate writes and setAvailability,
		// which left a pre-availability [] cached forever (empty start-time dropdown).
		this._startTimes = undefined;
		this._endTimes = undefined;
		this._updateAllDates(property);
		this._operations.clear();
		const startDate = DateTime.fromDateAndTime(this._startDate, this._startTime);
		const endDate = DateTime.fromDateAndTime(this._endDate, this._endTime);
		this._operations.tryCreateSegment(startDate, endDate);
	}

	private _updateAllDates(property: ModifiedProperty): void {
		let startTime: TimeOnly;

		if (property === ModifiedProperty.StartDate) {
			startTime = TimeOnly.fromComponents(0);

		} else {
			startTime = this._startTime;
		}

		const entry = this._getNextAvailablePrebookingEntry(DateTime.fromDateAndTime(this._startDate, startTime))
		if (entry == null) {
			return;
		}

		const {startDate, endDate} = entry;
		this._startDate = startDate.toDateOnly();
		this._startTime = startDate.toTimeOnly();

		if (property === ModifiedProperty.StartDate || this._startDate.compareTo(this._endDate) > 0 || !this.isSelectableEndDate(this._endDate)) {
			this._endDate = this._startDate;
		}

		let endTime: TimeOnly;
		if (this._startDate.equals(this._endDate)) {
			this._endDate = endDate.toDateOnly();
			endTime = endDate.toTimeOnly();

		} else {
			endTime = this._getNextAvailablePrebookingEntry(DateTime.fromDateAndTime(this._endDate, TimeOnly.zero()))?.endDate.toTimeOnly() ?? TimeOnly.zero();
		}

		if (property === ModifiedProperty.All || property !== ModifiedProperty.EndTime) {
			this._endTime = endTime;
		}
	}

	private _validateMaxConsecutiveDays(endDate: DateOnly): boolean {
		if (this._maxConsecutiveDays <= 0) {
			return true;
		}

		let dateCount = 0;
		for (let currentDate = this._startDate; currentDate < endDate; currentDate = currentDate.addDays(1)) {
			if (this._isUnavailable(currentDate)) {
				continue;
			}

			const e = this._getPrebookingTimes(currentDate);
			if (e == null || !e.available) {
				return false;
			}

			++dateCount;
		}

		return dateCount < this._maxConsecutiveDays;
	}

	private _getNextAvailablePrebookingEntry(startDate: DateTime): ParkingPrebookingAvailabilityEntry | undefined {
		const operations = this._availability;
		if (operations.length === 0) {
			return undefined;
		}

		const availableEntries = operations.filter(x => x.endDate > startDate && x.type === ParkingPrebookingAvailabilityType.Available);
		for (const entry of availableEntries) {
			if (entry.startDate >= startDate) {
				return entry;
			}
		}

		return undefined;
	}
}
